import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarRange, Send, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const currentMonth = () => new Date().toISOString().slice(0, 7);

export const MonthlySummary = ({ accounts = [] }) => {
  const [accountId, setAccountId] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!accountId && accounts.length > 0) setAccountId(accounts[0].id);
  }, [accounts, accountId]);

  useEffect(() => {
    if (!accountId || !month) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/accounts/${accountId}/summary`, { params: { month } })
      .then((res) => { if (!cancelled) setSummary(res.data); })
      .catch(() => { if (!cancelled) { setSummary(null); toast.error("No se pudo cargar el resumen"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, month]);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/accounts/${accountId}/summary/send`, null, { params: { month } });
      toast[res.data.email_status === "sent" ? "success" : "error"](res.data.email_status === "sent" ? `Resumen enviado a ${res.data.email}` : "No se pudo enviar el resumen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el resumen");
    } finally {
      setSending(false);
    }
  };

  if (accounts.length === 0) return null;

  return (
    <Card className="border-none shadow-md" data-testid="monthly-summary-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarRange className="w-5 h-5 text-brand-blue" /> Resumen mensual</CardTitle>
        <CardDescription>Cada día 1 enviamos a tu correo cómo creció la alcancía y quién aportó. Aquí puedes verlo o enviarlo ahora.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
          <div className="space-y-2">
            <Label>Alcancía</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="summary-account-select"><SelectValue placeholder="Elige una alcancía" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.child_name} {a.child_lastname}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Mes</Label>
            <Input type="month" data-testid="summary-month-input" value={month} max={currentMonth()} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <Button onClick={send} disabled={sending || !summary} data-testid="summary-send-btn" className="bg-brand-blue text-white font-bold rounded-xl h-10">
            <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar a mi correo"}
          </Button>
        </div>

        {summary && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="summary-stats">
            <div className="rounded-2xl bg-brand-lightGreen p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-bold">Saldo al cierre</p>
              <p className="text-2xl font-extrabold text-brand-dark" data-testid="summary-end-balance">${summary.end_balance.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl bg-brand-lightBlue p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Crecimiento</p>
              <p className="text-2xl font-extrabold text-brand-blue" data-testid="summary-growth">{summary.growth >= 0 ? "+" : "-"}${Math.abs(summary.growth).toLocaleString()}</p>
            </div>
            <div className="rounded-2xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-bold">Aportes</p>
              <p className="text-2xl font-extrabold text-brand-dark" data-testid="summary-contributions-count">{summary.contributions.length}</p>
            </div>
          </div>
        )}

        <div className={`rounded-2xl overflow-hidden border border-gray-200 bg-gray-100 transition-opacity ${loading ? "opacity-50" : ""}`}>
          {summary ? <iframe title="Resumen mensual" srcDoc={summary.html} data-testid="summary-iframe" className="w-full h-[560px] bg-white" sandbox="" /> : <p className="p-6 text-sm text-gray-500 text-center">Selecciona una alcancía para ver el resumen.</p>}
        </div>
      </CardContent>
    </Card>
  );
};
